import React, { useState, useMemo } from 'react';
import { View, Pressable, StyleSheet, Modal, ActivityIndicator, FlatList, TextInput } from 'react-native';
import { Feather } from '@expo/vector-icons';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import { useTranslation } from '../../i18n';

/**
 * BulkActionBar - Fixed bottom bar for bulk operations on selected reports
 * Shown when selectMode is active and items are selected
 */
const BulkActionBar = ({

  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onClose,
  onAssign,
  analysts = [],
  loading = false,
}) => {
  const { t } = useTranslation('admin');
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredAnalysts = useMemo(() => {
    if (!searchQuery.trim()) return analysts;
    const q = searchQuery.toLowerCase();
    return analysts.filter((a) => a.email.toLowerCase().includes(q));
  }, [analysts, searchQuery]);

  const handleAssign = (analystEmail) => {
    setShowAssignPicker(false);
    setSearchQuery('');
    onAssign(analystEmail);
  };

  const closeModal = () => {
    setShowAssignPicker(false);
    setSearchQuery('');
  };

  return (
    <>
      <View style={bulkStyles.container}>
        {/* Selected count + loading */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <CustomText font="semiBold" size={14} color={AppStyles.colorSet.textPrimary}>
            {t('bulkBar.selectedCount', { count: selectedCount })}
          </CustomText>
          {loading && <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />}
        </View>

        {/* Action buttons */}
        <View style={bulkStyles.actions}>
          {/* Select All */}
          {selectedCount < totalCount && (
            <Pressable
              style={({ pressed, hovered, focused }) => [
                bulkStyles.actionButton,
                { cursor: 'pointer' },
                (hovered || focused) && bulkStyles.actionButtonHovered,
                pressed && { opacity: 0.7 },
              ]}
              onPress={onSelectAll}
              disabled={loading}
            >
              <Feather name="check-square" size={16} color={AppStyles.colorSet.textSecondary} />
              <CustomText font="medium" size={12} color={AppStyles.colorSet.textSecondary}>
                {t('bulkBar.all')}
              </CustomText>
            </Pressable>
          )}

          {/* Clear */}
          <Pressable
            style={({ pressed, hovered, focused }) => [
              bulkStyles.actionButton,
              { cursor: 'pointer' },
              (hovered || focused) && bulkStyles.actionButtonHovered,
              pressed && { opacity: 0.7 },
            ]}
            onPress={onClearSelection}
            disabled={loading}
          >
            <Feather name="x" size={16} color={AppStyles.colorSet.textSecondary} />
            <CustomText font="medium" size={12} color={AppStyles.colorSet.textSecondary}>
              {t('bulkBar.clear')}
            </CustomText>
          </Pressable>

          {/* Assign */}
          <Pressable
            style={({ pressed, hovered, focused }) => [
              bulkStyles.actionButton,
              bulkStyles.actionButtonPrimary,
              { cursor: 'pointer' },
              (hovered || focused) && bulkStyles.actionButtonHovered,
              (pressed || loading) && { opacity: 0.7 },
            ]}
            onPress={() => setShowAssignPicker(true)}
            disabled={loading}
          >
            <Feather name="user-plus" size={16} color="#B45309" />
            <CustomText font="medium" size={12} color="#B45309">
              {t('bulkBar.assign')}
            </CustomText>
          </Pressable>

          {/* Close */}
          <Pressable
            style={({ pressed, hovered, focused }) => [
              bulkStyles.actionButton,
              bulkStyles.actionButtonDanger,
              { cursor: 'pointer' },
              (hovered || focused) && bulkStyles.actionButtonHovered,
              (pressed || loading) && { opacity: 0.7 },
            ]}
            onPress={onClose}
            disabled={loading}
          >
            <Feather name="check-circle" size={16} color={AppStyles.colorSet.success} />
            <CustomText font="medium" size={12} color={AppStyles.colorSet.success}>
              {t('bulkBar.close')}
            </CustomText>
          </Pressable>
        </View>
      </View>

      {/* Analyst picker modal */}
      <Modal
        visible={showAssignPicker}
        transparent
        animationType="fade"
        onRequestClose={closeModal}
      >
        <View style={bulkStyles.modalOverlay}>
          <Pressable style={StyleSheet.absoluteFill} onPress={closeModal} />
          <View style={bulkStyles.modalContent}>
            <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary} style={{ marginBottom: 12 }}>
              {t('bulk.assignTo', { count: selectedCount })}
            </CustomText>
            {/* Search input */}
            <View style={bulkStyles.searchContainer}>
              <Feather name="search" size={16} color={AppStyles.colorSet.textMuted} />
              <TextInput
                style={bulkStyles.searchInput}
                placeholder={t('bulkBar.searchAnalysts')}
                placeholderTextColor={AppStyles.colorSet.textMuted}
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoCorrect={false}
              />
              {searchQuery.length > 0 && (
                <Pressable onPress={() => setSearchQuery('')}>
                  <Feather name="x-circle" size={16} color={AppStyles.colorSet.textMuted} />
                </Pressable>
              )}
            </View>
            {/* Analyst list */}
            <FlatList
              data={filteredAnalysts}
              keyExtractor={(item) => item.email}
              keyboardShouldPersistTaps="handled"
              ListHeaderComponent={
                !searchQuery.trim() ? (
                  <Pressable
                    style={({ pressed, hovered, focused }) => [
                      bulkStyles.analystOption,
                      { cursor: 'pointer' },
                      (hovered || focused) && bulkStyles.analystOptionHovered,
                      pressed && { backgroundColor: AppStyles.colorSet.inputBackground },
                    ]}
                    onPress={() => handleAssign(null)}
                  >
                    <Feather name="user-x" size={16} color={AppStyles.colorSet.textMuted} />
                    <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary} style={{ marginLeft: 8 }}>
                      {t('bulkBar.unassigned')}
                    </CustomText>
                  </Pressable>
                ) : null
              }
              renderItem={({ item }) => (
                <Pressable
                  style={({ pressed, hovered, focused }) => [
                    bulkStyles.analystOption,
                    { cursor: 'pointer' },
                    (hovered || focused) && bulkStyles.analystOptionHovered,
                    pressed && { backgroundColor: AppStyles.colorSet.inputBackground },
                  ]}
                  onPress={() => handleAssign(item.email)}
                >
                  <Feather name="user" size={16} color={AppStyles.colorSet.textSecondary} />
                  <CustomText font="regular" size={14} color={AppStyles.colorSet.textPrimary} style={{ marginLeft: 8 }}>
                    {item.email}
                  </CustomText>
                </Pressable>
              )}
              ListEmptyComponent={
                <CustomText font="regular" size={14} color={AppStyles.colorSet.textMuted} style={{ marginTop: 8, textAlign: 'center' }}>
                  {searchQuery.trim() ? t('bulkBar.noMatchingAnalysts') : t('bulkBar.noAnalysts')}
                </CustomText>
              }
            />
            <Pressable
              style={({ pressed, hovered, focused }) => [
                bulkStyles.cancelButton,
                { cursor: 'pointer' },
                (hovered || focused) && { opacity: 0.85 },
                pressed && { opacity: 0.7 },
              ]}
              onPress={closeModal}
            >
              <CustomText font="medium" size={14} color={AppStyles.colorSet.textSecondary}>
                {t('common:actions.cancel')}
              </CustomText>
            </Pressable>
          </View>
        </View>
      </Modal>
    </>
  );
};

const bulkStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderTopWidth: 1,
    borderTopColor: AppStyles.colorSet.cardBorder,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.inputBorder,
    backgroundColor: AppStyles.colorSet.cardBackground,
  },
  // Web hover/focus feedback for action buttons — neutral lift so it works
  // across the default / primary / danger variants without recoloring them
  actionButtonHovered: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.18,
    shadowRadius: 4,
    elevation: 2,
  },
  actionButtonPrimary: {
    backgroundColor: `${AppStyles.colorSet.brandPrimary}25`,
    borderColor: AppStyles.colorSet.brandPrimary,
  },
  actionButtonDanger: {
    backgroundColor: AppStyles.colorSet.successLight,
    borderColor: AppStyles.colorSet.success,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    padding: 20,
    width: '90%',
    maxWidth: 400,
    maxHeight: '70%',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.inputBorder,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    color: AppStyles.colorSet.textPrimary,
    padding: 0,
  },
  analystOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.cardBorder,
  },
  analystOptionHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  cancelButton: {
    alignItems: 'center',
    paddingVertical: 12,
    marginTop: 8,
  },
});

export default BulkActionBar;
